import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Tooltip,
  Badge
} from '@mui/material';
import { motion } from 'framer-motion';
import { Star, QuestionMark, CreditCard, EmojiEvents } from '@mui/icons-material';
import { Spread } from '../types/spread';
import { spreadsService } from '../services/spreadsService';
import { authService } from '../services/authService';
import { errorHandler } from '../utils/errorHandler';

const TarotReading: React.FC = () => {
  const navigate = useNavigate();
  const [selectedSpread, setSelectedSpread] = useState<Spread | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [spreads, setSpreads] = useState<Spread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userPoints, setUserPoints] = useState<number>(0);

  useEffect(() => {
    loadSpreads();
    // Kullanıcı oturum durumunu dinle
    const unsubscribe = authService.onAuthStateChange(async (user) => {
      setIsLoggedIn(!!user);
      if (user) {
        try {
          const userData = await authService.getUserProfile(user.uid);
          setUserPoints(userData?.points || 0);
        } catch (err) {
          errorHandler.handleError(err, 'Error loading user points');
        }
      } else {
        setUserPoints(0);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadSpreads = async () => {
    try {
      setLoading(true);
      const data = await spreadsService.getAll();
      setSpreads(data);
    } catch (err) {
      errorHandler.handleError(err, 'Error loading spreads');
      setError('Tarot falı seçenekleri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (spread: Spread) => {
    setSelectedSpread(spread);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleBookAppointment = (type: 'regular' | 'points' = 'regular') => {
    if (selectedSpread) {
      if (!isLoggedIn) {
        handleCloseDialog();
        navigate('/login');
        return;
      }
      const flag = type === 'points' ? 2 : 1;
      navigate(`/booking?spreadId=${selectedSpread.id}&type=${type}&flag=${flag}`);
    }
  };

  const hasEnoughPoints = (spread: Spread): boolean => {
    return userPoints >= (spread?.price || 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ mt: 8, mb: 12 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            component="h1"
            align="center"
            className="gradient-text"
            sx={{ mb: 6 }}
          >
            Tarot Seçenekleri
          </Typography>

          <Grid container spacing={4}>
            {spreads.map((spread) => (
              <Grid item xs={12} md={6} key={spread.id}>
                <Card 
                  elevation={3}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="300"
                    image={spread.imageUrl}
                    alt={spread.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h5">
                        {spread.title}
                      </Typography>
                      <Tooltip title="Bu falı tamamladığınızda kazanacağınız puan">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'primary.main',
                            color: 'white',
                            borderRadius: '20px',
                            padding: '4px 12px',
                            gap: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            }
                          }}
                        >
                          <EmojiEvents fontSize="small" />
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {spread.points} Puan
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Box>
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      paragraph
                    >
                      {spread.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<Star />}
                        label={`${spread.deckCount} Deste`}
                        color="primary"
                      />
                      <Chip
                        icon={<QuestionMark />}
                        label={`Soru Hakkı: ${spread.readingDuration}`}
                        color="primary"
                      />
                      <Chip
                        icon={<CreditCard />}
                        label={`${spread.price} TL`}
                        color="primary"
                      />
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => handleOpenDialog(spread)}
                      sx={{
                        borderRadius: '25px',
                        py: 1.5
                      }}
                    >
                      Randevu Al
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedSpread && (
          <>
            <DialogTitle component="div">
              <Typography variant="h4" className="gradient-text">
                {selectedSpread.title}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Açılım Hakkında
                </Typography>
                <Typography variant="body1">
                  {selectedSpread.description}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Detaylar
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Star color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={`${selectedSpread.deckCount} deste kullanılır`} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <QuestionMark color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Seans Süreleri"
                      secondary={
                        <>
                          <Typography component="span" variant="body2" display="block">
                            Soru Hakkı: {selectedSpread.readingDuration}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CreditCard color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={`Ücret: ${selectedSpread.price} TL`} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmojiEvents color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={`Gerekli Puan: ${selectedSpread.price || 0} Puan (${selectedSpread.price} TL)`}
                      secondary={
                        isLoggedIn && !hasEnoughPoints(selectedSpread) ? (
                          <Typography component="span" variant="body2" color="error">
                            Yetersiz puan (Mevcut: {userPoints} puan)
                          </Typography>
                        ) : null
                      }
                    />
                  </ListItem>
                </List>
              </Box>
            </DialogContent>
            <DialogActions 
              sx={{ 
                p: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
                gap: { xs: 1, sm: 0 }
              }}
            >
              {isLoggedIn ? (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleBookAppointment('points')}
                    fullWidth
                    disabled={!hasEnoughPoints(selectedSpread)}
                    sx={{
                      borderRadius: '25px',
                      py: { xs: 1, sm: 'inherit' },
                      mb: { xs: 1, sm: 0 },
                      mr: { xs: 0, sm: 1 }
                    }}
                  >
                    {hasEnoughPoints(selectedSpread) ? `Puan ile Öde (${selectedSpread.price} Puan)` : `Yetersiz Puan (${userPoints}/${selectedSpread.price})`}
                  </Button>
                  <Button 
                    onClick={handleCloseDialog}
                    fullWidth
                    sx={{
                      mb: { xs: 1, sm: 0 },
                      order: { xs: 3, sm: 1 }
                    }}
                  >
                    Kapat
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleBookAppointment()}
                    fullWidth
                    sx={{
                      borderRadius: '25px',
                      py: { xs: 1, sm: 'inherit' },
                      order: { xs: 2, sm: 2 }
                    }}
                  >
                    Randevu Al
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    onClick={handleCloseDialog}
                    sx={{ mr: 1 }}
                  >
                    Kapat
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/login')}
                    sx={{
                      borderRadius: '25px',
                      py: { xs: 1, sm: 'inherit' }
                    }}
                  >
                    Giriş Yap
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default TarotReading; 